/**
 * Cross-tab slot coordinator for playground mode.
 *
 * Uses localStorage + `storage` events to assign each browser tab a unique slot:
 *   Slot 0  = primary tab (Google sign-in)
 *   Slot 1+ = secondary tabs (synthetic "Player N" profiles)
 *   Slot -1 = waiting for primary tab to complete Google sign-in
 *
 * Tab identity is stored in sessionStorage (survives reload, unique per tab).
 * Stale tabs are pruned via a heartbeat mechanism (every 10 s, stale after 30 s).
 */

const REGISTRY_KEY = 'rundot-playground-tabs'
const TAB_ID_KEY = 'rundot-playground-tab-id'
const STALE_THRESHOLD_MS = 30_000
const HEARTBEAT_INTERVAL_MS = 10_000

export interface TabRegistry {
  tabs: Record<string, { slot: number; playerId: string | null; timestamp: number }>
  googleSignedIn: boolean
}

export const PLAYER_POOL: Array<string | null> = [null, 'player1', 'player2', 'player3']

// ─── Tab ID ─────────────────────────────────────────────────────────────────

function getOrCreateTabId(): string {
  try {
    let id = sessionStorage.getItem(TAB_ID_KEY)
    if (id) return id
    id = crypto.randomUUID()
    sessionStorage.setItem(TAB_ID_KEY, id)
    return id
  } catch {
    // Fallback if sessionStorage is unavailable
    return crypto.randomUUID()
  }
}

// ─── Registry helpers ───────────────────────────────────────────────────────

function readRegistry(): TabRegistry {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY)
    if (raw) {
      const parsed = JSON.parse(raw) as TabRegistry
      if (parsed && typeof parsed.tabs === 'object') return parsed
    }
  } catch {
    // corrupt or missing — return default
  }
  return { tabs: {}, googleSignedIn: false }
}

function writeRegistry(registry: TabRegistry): void {
  try {
    localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry))
  } catch {
    // ignore
  }
}

function pruneStale(registry: TabRegistry): void {
  const now = Date.now()
  for (const [tabId, entry] of Object.entries(registry.tabs)) {
    if (now - entry.timestamp > STALE_THRESHOLD_MS) {
      delete registry.tabs[tabId]
    }
  }
}

function claimedSlots(registry: TabRegistry): Set<number> {
  return new Set(Object.values(registry.tabs).map((e) => e.slot))
}

function lowestAvailableSlot(claimed: Set<number>, startFrom: number): number {
  let slot = startFrom
  while (claimed.has(slot)) slot++
  return slot
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Assign this tab a slot in the registry.
 *
 * - If no tabs and Google not yet signed in → slot 0 (primary).
 * - If googleSignedIn → lowest unclaimed slot starting from 1.
 * - If not googleSignedIn but other tabs exist → slot -1 (waiting).
 */
export function getOrAssignSlot(): { slot: number; tabId: string; playerId: string | null } {
  const tabId = getOrCreateTabId()
  const registry = readRegistry()
  pruneStale(registry)

  // If this tab already has a slot, refresh timestamp and return it
  if (registry.tabs[tabId]) {
    registry.tabs[tabId].timestamp = Date.now()
    writeRegistry(registry)
    const entry = registry.tabs[tabId]
    return { slot: entry.slot, tabId, playerId: entry.playerId }
  }

  const claimed = claimedSlots(registry)
  let slot: number

  if (registry.googleSignedIn) {
    // Google is signed in — assign as secondary (slot 1+)
    // Unless slot 0 is unclaimed, in which case this tab reclaims it
    if (!claimed.has(0)) {
      slot = 0
    } else {
      slot = lowestAvailableSlot(claimed, 1)
    }
  } else if (claimed.size === 0) {
    // First tab, no Google sign-in yet → primary
    slot = 0
  } else {
    // Other tabs exist but Google not signed in → waiting
    slot = -1
  }

  // Auto-assign playerId based on slot
  let playerId: string | null = null
  if (slot >= 0) {
    const claimedPlayerIds = new Set(
      Object.entries(registry.tabs)
        .filter(([id]) => id !== tabId)
        .map(([, e]) => e.playerId)
    )

    if (slot === 0) {
      // Slot 0 defaults to null (Google/Default)
      playerId = null
    } else {
      // Secondary slots: first unclaimed non-null from pool, or 'anonymous'
      const available = PLAYER_POOL.filter(
        pid => pid !== null && !claimedPlayerIds.has(pid)
      )
      playerId = available.length > 0 ? available[0] : 'anonymous'
    }

    registry.tabs[tabId] = { slot, playerId, timestamp: Date.now() }
    writeRegistry(registry)
  }

  return { slot, tabId, playerId }
}

/**
 * Update the playerId for an existing tab entry.
 */
export function setPlayerId(tabId: string, playerId: string | null): void {
  const registry = readRegistry()
  if (registry.tabs[tabId]) {
    registry.tabs[tabId].playerId = playerId
    registry.tabs[tabId].timestamp = Date.now()
    writeRegistry(registry)
  }
}

/**
 * Clear the entire registry (all tabs + googleSignedIn flag).
 * Used on sign-out so every tab starts fresh after reload.
 */
export function clearRegistry(): void {
  writeRegistry({ tabs: {}, googleSignedIn: false })
}

/**
 * True when another LIVE tab (heartbeat-fresh) already holds `playerId`.
 * Used by the URL-forced identity path (?rundotPlayer=) to warn when the
 * same id was forced in two tabs of the same browser — they would collide
 * on one synthetic uid and the room server would reject the second with
 * DUPLICATE_SESSION.
 */
export function hasLiveTabWithPlayerId(playerId: string | null, excludeTabId?: string): boolean {
  const registry = readRegistry()
  pruneStale(registry)
  return Object.entries(registry.tabs).some(
    ([id, entry]) => id !== excludeTabId && entry.playerId === playerId,
  )
}

/**
 * Update the `googleSignedIn` flag (called by the primary tab after Google sign-in).
 */
export function setGoogleSignedIn(value: boolean): void {
  const registry = readRegistry()
  registry.googleSignedIn = value
  writeRegistry(registry)
}

/**
 * Read the `googleSignedIn` flag.
 */
export function isGoogleSignedIn(): boolean {
  return readRegistry().googleSignedIn
}

/**
 * Listen for `storage` events to detect when another tab completes Google sign-in.
 * Returns an unsubscribe function.
 */
export function onGoogleSignedIn(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== REGISTRY_KEY) return
    try {
      const registry = JSON.parse(e.newValue || '{}') as TabRegistry
      if (registry.googleSignedIn) {
        callback()
      }
    } catch {
      // ignore parse errors
    }
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * Listen for `storage` events to detect when another tab signs out of Google.
 * Returns an unsubscribe function.
 */
export function onGoogleSignedOut(callback: () => void): () => void {
  const handler = (e: StorageEvent) => {
    if (e.key !== REGISTRY_KEY) return
    try {
      const newRegistry = JSON.parse(e.newValue || '{}') as TabRegistry
      if (newRegistry.googleSignedIn) return // still signed in — nothing to do

      // Only fire if the PREVIOUS state had googleSignedIn === true.
      // Without this guard, any registry write while googleSignedIn is already
      // false (heartbeats, slot claims, clearRegistry) would trigger a reload.
      const oldRegistry = JSON.parse(e.oldValue || '{}') as TabRegistry
      if (oldRegistry.googleSignedIn) {
        callback()
      }
    } catch { /* ignore */ }
  }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/**
 * Start a heartbeat that refreshes this tab's timestamp every 10 s.
 * Returns a stop function.
 */
export function startHeartbeat(tabId: string): () => void {
  const interval = setInterval(() => {
    const registry = readRegistry()
    if (registry.tabs[tabId]) {
      registry.tabs[tabId].timestamp = Date.now()
      writeRegistry(registry)
    }
  }, HEARTBEAT_INTERVAL_MS)

  return () => clearInterval(interval)
}
